import { useMemo } from 'react'
import { keepPreviousData } from '@tanstack/react-query'

import { getPromotionRakeRaceRetrieveQueryKey, usePromotionRakeRaceRetrieve } from '@/api/gen'
import { DataTable } from '@/components/data-table'
import { tableQueryState } from '@/components/table-query-state'
import { useAuth } from '@/hooks/use-auth'
import { useRacePrizes } from '@/pages/promotion/hooks/use-race-prizes'
import type { RakeRaceRow } from '@/pages/promotion/hooks/use-rake-race-table-cols'
import { useRakeRaceTableCols } from '@/pages/promotion/hooks/use-rake-race-table-cols'
import { safeNumber } from '@/utils/helpers'

import { USER_RANK_ROW } from './constants'

export const RakeRaceTable = () => {
  const prizes = useRacePrizes()
  const { isInitialized, isAuthorized } = useAuth()
  const { data, error, isError, isFetching, refetch } = usePromotionRakeRaceRetrieve({
    query: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 10,
      placeholderData: keepPreviousData,
      queryKey: [...getPromotionRakeRaceRetrieveQueryKey(), isAuthorized],
    },
  })

  const matchedData = useMemo(() => {
    return prizes.map((col) => ({
      prize: col.title,
      prizeValue: col.prize,
      id: col.id,
      rank: col.id,
      score: `${safeNumber(data?.leaderboard.find((row) => row.rank === col.id)?.score ?? 0)}`,
    }))
  }, [data, prizes])

  const columns = useRakeRaceTableCols(matchedData)

  const queryState = tableQueryState({
    error,
    hasData: data !== undefined,
    isError,
    isFetching,
    isInitialized,
    refetch,
  })
  const { loading } = queryState

  const userStatus = data?.user_status
  // Gated on the FIRST load rather than on any fetch, so a background refetch does not drop the
  // line. Also gated on `isAuthorized`: the query key carries auth, so the key flip on logout
  // renders the signed-in payload once more before the hard navigation, and a row labelled "You"
  // should not be in that last frame.
  const showUserRow =
    isAuthorized &&
    !loading &&
    data !== undefined &&
    data.leaderboard.length > 0 &&
    userStatus !== undefined &&
    userStatus.rank !== 0

  // The user's own standing led the table as a DataGridPro pinned row. It is a REAL row here,
  // prepended to the data: pinning only ever meant "render first" for this table, which unset
  // the grid's scroll cap and so never had a viewport for a pinned row to stick to.
  const rows: RakeRaceRow[] = showUserRow
    ? [
        {
          id: USER_RANK_ROW,
          score: userStatus.score,
          rank: userStatus.rank,
          prize: '',
          prizeValue: 0,
        },
        ...matchedData,
      ]
    : matchedData

  return (
    <DataTable
      uncapped
      columns={columns}
      data={rows}
      // One of two tables off the DS default density (the other is free-tournament-tickets): the
      // user's own line stacks its rank over a "You" label, and two `body2` lines plus `gap-2`
      // measures 48px, which the 40px `standard` row clips. `comfortable` (50px) fits it.
      density="comfortable"
      getRowId={(row) => String(row.id)}
      {...queryState}
      // The tint DataGridPro painted on its pinned top row, so the user's own line still reads
      // apart from the ladder below it. Reads the DATA's id, not `row.id` — that is whatever
      // `getRowId` returned, so keying off it would break silently if the id shape changed.
      getRowProps={(row) =>
        row.original.id === USER_RANK_ROW ? { className: 'bg-primary-main/8' } : {}
      }
    />
  )
}
